import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, RefreshCw, Edit3, RotateCcw, Save, X, History, Check } from "lucide-react";
import {
    adminOpsListHeroes,
    adminOpsRegenerateHeroes,
    adminOpsListHeroPrompts,
    adminOpsSaveHeroPrompt,
    adminOpsResetHeroPrompt,
    adminOpsRegenerateOneHero,
    adminOpsListHeroRevisions,
    adminOpsRestoreHeroRevision,
    adminOpsRevertHeroPng,
} from "@/lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function formatKB(bytes) {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function formatAge(mtime) {
    if (!mtime) return "never";
    const secs = Math.floor(Date.now() / 1000 - mtime);
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)} h ago`;
    return `${Math.floor(secs / 86400)} d ago`;
}

// Hero regeneration + prompt-editing admin card.
// One UI for: viewing each hero's current PNG/WebP, editing its prompt,
// saving / resetting, and regenerating either one hero or all four.
export default function HeroRegeneration() {
    const [heroes, setHeroes] = useState([]);
    const [prompts, setPrompts] = useState({});
    const [editingKey, setEditingKey] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [revisions, setRevisions] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [busyAll, setBusyAll] = useState(false);
    const [busyOne, setBusyOne] = useState(null);
    const [lastResult, setLastResult] = useState(null);
    const [diffModal, setDiffModal] = useState(null);  // { hero, beforeUrl, afterUrl, ts }

    const load = async () => {
        try {
            const [h, p] = await Promise.all([
                adminOpsListHeroes(),
                adminOpsListHeroPrompts(),
            ]);
            setHeroes(h.heroes || []);
            const map = {};
            for (const row of (p.prompts || [])) map[row.key] = row;
            setPrompts(map);
        } catch {
            /* endpoint may not exist on older deploys */
        }
    };

    useEffect(() => { load(); }, []);

    const onRegenerateAll = async () => {
        if (!window.confirm(
            "Re-render ALL 4 claymation hero images via Gemini Nano Banana?\n\n" +
            "Takes ~10-15s. Existing PNGs will be overwritten. The new images " +
            "will appear immediately on /learn, /tools, /for-investors, and /how-it-works."
        )) return;
        setBusyAll(true);
        setLastResult(null);
        const t0 = Date.now();
        try {
            const r = await adminOpsRegenerateHeroes();
            setLastResult(r);
            const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
            if (r.ok) toast.success(`All heroes regenerated in ${elapsed}s`);
            else toast.error("Partial failure — see details below.");
            await load();
        } catch (e) {
            toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
        } finally {
            setBusyAll(false);
        }
    };

    const onRegenerateOne = async (key) => {
        setBusyOne(key);
        const t0 = Date.now();
        try {
            const r = await adminOpsRegenerateOneHero(key);
            const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
            if (r.ok) {
                toast.success(`${key} regenerated in ${elapsed}s`);
                await load();
                // Open the before/after diff modal if a backup was made.
                if (r.backup_available) {
                    const hero = heroes.find((h) => h.key === key);
                    const filename = hero?.name || "";
                    const ts = Date.now();
                    setDiffModal({
                        hero: { key, title: hero?.title || key, page: hero?.page || "" },
                        beforeUrl: `${API_BASE}/api/admin/ops/heroes/${key}/preview/previous?t=${ts}`,
                        afterUrl: `${API_BASE}/${filename}.png?t=${ts}`,
                        ts,
                    });
                }
            } else {
                toast.error(`Failed: ${r.error || "unknown"}`);
            }
        } catch (e) {
            toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
        } finally {
            setBusyOne(null);
        }
    };

    const onKeepNewVersion = () => {
        setDiffModal(null);
        toast.success("New version kept.");
    };

    const onRevertPng = async () => {
        if (!diffModal) return;
        const key = diffModal.hero.key;
        try {
            await adminOpsRevertHeroPng(key);
            toast.success("Reverted to previous PNG.");
            setDiffModal(null);
            await load();
        } catch (e) {
            toast.error(`Revert failed: ${e.response?.data?.detail || e.message}`);
        }
    };

    const onEdit = async (key) => {
        setEditingKey(key);
        setEditValue(prompts[key]?.current_prompt || "");
        setShowHistory(false);
        try {
            const r = await adminOpsListHeroRevisions(key);
            setRevisions(r.revisions || []);
        } catch {
            setRevisions([]);
        }
    };

    const onRestoreRevision = async (revisionId) => {
        if (!editingKey) return;
        if (!window.confirm("Restore this older prompt? Your current prompt will be archived to history first (reversible).")) return;
        try {
            await adminOpsRestoreHeroRevision(editingKey, revisionId);
            toast.success("Restored. Click Regenerate to apply.");
            const fresh = await adminOpsListHeroRevisions(editingKey);
            setRevisions(fresh.revisions || []);
            await load();
            // Reload editValue from refreshed prompts
            const r = await adminOpsListHeroPrompts();
            const row = (r.prompts || []).find((x) => x.key === editingKey);
            if (row) setEditValue(row.current_prompt || "");
        } catch (e) {
            toast.error(`Restore failed: ${e.response?.data?.detail || e.message}`);
        }
    };

    const onSavePrompt = async (key) => {
        try {
            await adminOpsSaveHeroPrompt(key, editValue);
            toast.success("Prompt saved. Click Regenerate to apply.");
            setEditingKey(null);
            await load();
        } catch (e) {
            toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
        }
    };

    const onResetPrompt = async (key) => {
        if (!window.confirm("Reset this hero's prompt back to the registry default?")) return;
        try {
            await adminOpsResetHeroPrompt(key);
            toast.success("Prompt reset to default.");
            setEditingKey(null);
            await load();
        } catch (e) {
            toast.error(`Reset failed: ${e.response?.data?.detail || e.message}`);
        }
    };

    return (
        <div
            className="rounded-xl border border-bl bg-white p-5"
            data-testid="hero-regeneration-card"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                        <ImageIcon className="w-3.5 h-3.5" />
                        Marketing hero images
                    </div>
                    <h3 className="mt-2 font-display font-semibold text-bl text-lg">
                        Claymation heroes — edit prompt + regenerate
                    </h3>
                    <p className="mt-1 text-sm text-bl-muted leading-relaxed">
                        Tweak the prompt for any hero, save it, then regenerate.
                        Custom prompts persist in MongoDB; &quot;Reset&quot; reverts to the
                        registry default. ~3-10s per hero.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onRegenerateAll}
                    disabled={busyAll || !!busyOne}
                    data-testid="hero-regeneration-trigger"
                    className="bl-btn-primary gap-2 shrink-0 disabled:opacity-60"
                >
                    <RefreshCw className={`w-4 h-4 ${busyAll ? "animate-spin" : ""}`} />
                    {busyAll ? "Regenerating…" : "Regenerate all"}
                </button>
            </div>

            {heroes.length > 0 && (
                <div className="mt-5 space-y-3" data-testid="hero-regeneration-list">
                    {heroes.map((h) => {
                        const p = prompts[h.key];
                        const isEditing = editingKey === h.key;
                        return (
                            <div
                                key={h.key}
                                className="rounded-lg border border-bl"
                                data-testid={`hero-row-${h.key}`}
                            >
                                <div className="flex items-center justify-between gap-3 px-4 py-3 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-bl">
                                            {h.title}
                                        </div>
                                        <div className="text-[10px] text-bl-muted font-mono-bl mt-0.5">
                                            {h.page} · {h.name} · {formatKB(h.png_bytes)} PNG /
                                            {" "}{formatKB(h.webp_bytes)} WebP · {formatAge(h.png_mtime)}
                                            {p && !p.is_default && (
                                                <span className="ml-2 text-bl-primary">· custom prompt</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {!isEditing && (
                                            <button
                                                type="button"
                                                onClick={() => onEdit(h.key)}
                                                disabled={busyAll || !!busyOne}
                                                data-testid={`hero-edit-${h.key}`}
                                                className="bl-btn-secondary gap-1.5 text-xs px-3 py-1.5"
                                            >
                                                <Edit3 className="w-3 h-3" />
                                                Edit prompt
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => onRegenerateOne(h.key)}
                                            disabled={busyAll || !!busyOne}
                                            data-testid={`hero-regen-${h.key}`}
                                            className="bl-btn-primary gap-1.5 text-xs px-3 py-1.5 disabled:opacity-60"
                                        >
                                            <RefreshCw className={`w-3 h-3 ${busyOne === h.key ? "animate-spin" : ""}`} />
                                            {busyOne === h.key ? "…" : "Regen"}
                                        </button>
                                    </div>
                                </div>

                                {isEditing && p && (
                                    <div className="border-t border-bl bg-bl-cream px-4 py-3">
                                        <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted mb-2">
                                            Prompt (Gemini Nano Banana)
                                        </div>
                                        <textarea
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            rows={12}
                                            data-testid={`hero-prompt-textarea-${h.key}`}
                                            className="w-full text-xs font-mono-bl bg-white border border-bl rounded-md p-3 focus:outline-none focus:ring-1 focus:ring-bl-primary"
                                            spellCheck={false}
                                        />
                                        <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                                            <span className="text-[10px] text-bl-muted font-mono-bl">
                                                {editValue.length.toLocaleString()} chars
                                                {p.is_default
                                                    ? " · using registry default"
                                                    : ` · custom (saved by ${p.updated_by || "admin"})`}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowHistory((s) => !s)}
                                                    disabled={revisions.length === 0}
                                                    data-testid={`hero-history-toggle-${h.key}`}
                                                    className="bl-btn-secondary gap-1.5 text-xs px-3 py-1.5 disabled:opacity-50"
                                                    title={revisions.length === 0 ? "No prior revisions yet" : ""}
                                                >
                                                    <History className="w-3 h-3" />
                                                    History ({revisions.length})
                                                </button>
                                                {!p.is_default && (
                                                    <button
                                                        type="button"
                                                        onClick={() => onResetPrompt(h.key)}
                                                        data-testid={`hero-reset-${h.key}`}
                                                        className="bl-btn-secondary gap-1.5 text-xs px-3 py-1.5"
                                                    >
                                                        <RotateCcw className="w-3 h-3" />
                                                        Reset to default
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingKey(null)}
                                                    data-testid={`hero-cancel-${h.key}`}
                                                    className="bl-btn-secondary gap-1.5 text-xs px-3 py-1.5"
                                                >
                                                    <X className="w-3 h-3" />
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => onSavePrompt(h.key)}
                                                    data-testid={`hero-save-${h.key}`}
                                                    disabled={!editValue.trim()}
                                                    className="bl-btn-primary gap-1.5 text-xs px-3 py-1.5 disabled:opacity-60"
                                                >
                                                    <Save className="w-3 h-3" />
                                                    Save prompt
                                                </button>
                                            </div>
                                        </div>

                                        {showHistory && revisions.length > 0 && (
                                            <div className="mt-4 rounded-md border border-bl bg-white p-3" data-testid={`hero-history-panel-${h.key}`}>
                                                <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted mb-2">
                                                    Last {revisions.length} {revisions.length === 1 ? "revision" : "revisions"} (newest first)
                                                </div>
                                                <ul className="space-y-2">
                                                    {revisions.map((rev) => (
                                                        <li
                                                            key={rev.revision_id}
                                                            className="rounded border border-bl px-3 py-2 flex items-start justify-between gap-3"
                                                            data-testid={`hero-revision-${rev.revision_id}`}
                                                        >
                                                            <div className="min-w-0 flex-1">
                                                                <div className="text-[10px] font-mono-bl text-bl-muted">
                                                                    {(rev.archived_at || rev.saved_at || "").slice(0, 19).replace("T", " ")}
                                                                    {rev.saved_by ? ` · ${rev.saved_by}` : ""}
                                                                </div>
                                                                <div className="mt-1 text-[11px] text-bl leading-snug line-clamp-2 break-words">
                                                                    {(rev.prompt || "").slice(0, 200)}
                                                                    {(rev.prompt || "").length > 200 ? "…" : ""}
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-col gap-1 shrink-0">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setEditValue(rev.prompt || "")}
                                                                    data-testid={`hero-revision-load-${rev.revision_id}`}
                                                                    className="text-[10px] text-bl-primary hover:underline"
                                                                >
                                                                    Load into editor
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => onRestoreRevision(rev.revision_id)}
                                                                    data-testid={`hero-revision-restore-${rev.revision_id}`}
                                                                    className="text-[10px] text-bl-primary hover:underline"
                                                                >
                                                                    Restore live
                                                                </button>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {lastResult && (
                <div
                    className="mt-4 rounded-lg border border-bl bg-bl-cream px-3 py-2 text-xs"
                    data-testid="hero-regeneration-result"
                >
                    <div className="font-mono-bl text-bl-muted mb-1">
                        Last bulk run: {lastResult.ok ? "✓ all ok" : "⚠ partial"} ·{" "}
                        {(lastResult.total_ms / 1000).toFixed(1)}s
                    </div>
                    {(lastResult.heroes || []).map((s) => (
                        <div key={s.key} className="flex items-center justify-between py-0.5">
                            <span className={s.ok ? "text-bl" : "text-rust"}>
                                {s.ok ? "✓" : "✗"} {s.key}
                                {!s.ok && s.error ? ` — ${s.error}` : ""}
                            </span>
                            <span className="font-mono-bl text-bl-muted">{s.ms} ms</span>
                        </div>
                    ))}
                </div>
            )}

            {diffModal && (
                <div
                    className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
                    onClick={(e) => { if (e.target === e.currentTarget) setDiffModal(null); }}
                    data-testid="hero-diff-modal"
                >
                    <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-bl">
                            <div>
                                <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    Compare result
                                </div>
                                <h3 className="mt-1 font-display font-semibold text-bl text-lg">
                                    {diffModal.hero.title}
                                </h3>
                                <p className="mt-0.5 text-xs text-bl-muted">
                                    {diffModal.hero.page} · keep the new render or revert to the previous PNG
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setDiffModal(null)}
                                data-testid="hero-diff-close"
                                className="text-bl-muted hover:text-bl"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4 p-6 overflow-y-auto">
                            <div data-testid="hero-diff-before">
                                <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted mb-2">
                                    Before (previous PNG)
                                </div>
                                <div className="rounded-xl border border-bl bg-bl-cream overflow-hidden">
                                    <img
                                        src={diffModal.beforeUrl}
                                        alt="Previous version"
                                        className="w-full h-auto block"
                                    />
                                </div>
                            </div>
                            <div data-testid="hero-diff-after">
                                <div className="text-[10px] tracking-[0.16em] uppercase text-bl-primary mb-2">
                                    After (just regenerated)
                                </div>
                                <div className="rounded-xl border-2 border-bl-primary bg-bl-cream overflow-hidden">
                                    <img
                                        src={diffModal.afterUrl}
                                        alt="New version"
                                        className="w-full h-auto block"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-bl bg-bl-surface">
                            <button
                                type="button"
                                onClick={onRevertPng}
                                data-testid="hero-diff-revert"
                                className="bl-btn-secondary gap-2"
                            >
                                <RotateCcw className="w-4 h-4" />
                                Revert to previous
                            </button>
                            <button
                                type="button"
                                onClick={onKeepNewVersion}
                                data-testid="hero-diff-keep"
                                className="bl-btn-primary gap-2"
                            >
                                <Check className="w-4 h-4" />
                                Keep new version
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
